import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ClipboardList, Users, ShieldAlert, Layers, Target, Loader2,
  UserRound, TrendingUp, AlertTriangle, ShieldCheck, ChevronRight, X, Download, Compass, CheckCircle2,
  Sparkles, RefreshCw, ChevronDown, Database, LineChart,
} from 'lucide-react';
import { CapabilityAnalytics } from '../components/capability/CapabilityAnalytics';
import { CoverageBar, CohortTags, RecChips, RISK } from '../components/capability/capabilityShared';
import { MemberDrawer } from '../components/capability/MemberDrawer';
import { DelegatedPlansDialog } from '../components/capability/DelegatedPlansDialog';
import { ImpactBriefDialog } from '../components/capability/ImpactBriefDialog';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import PageToolbar from '../components/layout/PageToolbar';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useDeepLinkOrg } from '../hooks/useDeepLinkOrg';

function StatTile({ icon: Icon, label, value, sub, tone = 'slate', testId }) {
  const tones = {
    slate: 'text-slate-700 bg-slate-100', emerald: 'text-emerald-700 bg-emerald-100',
    amber: 'text-amber-700 bg-amber-100', rose: 'text-rose-700 bg-rose-100', cyan: 'text-cyan-700 bg-cyan-100',
  };
  return (
    <Card className="border border-slate-200 shadow-sm" data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center shrink-0', tones[tone])}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-2xl font-extrabold text-slate-900 leading-none tabular-nums">{value}</p>
          <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mt-1">{label}</p>
          {sub && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CapabilityInventoryPage({ embedded = false, initialView, coverageMode = false }) {
  const theme = getLightSectionTheme('PE');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { viewAsOrg, isSuperAdmin } = useAuth();
  const urlOrgId = useDeepLinkOrg();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(urlOrgId || viewAsOrg?.id || '');
  const [view, setView] = useState(() => (initialView || (searchParams.get('view') === 'analytics' ? 'analytics' : 'inventory')));
  const changeView = useCallback((v) => {
    setView(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'analytics') next.set('view', 'analytics'); else next.delete('view');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [roles, setRoles] = useState([]);
  const [roleSearch, setRoleSearch] = useState('');
  const [roleStatus, setRoleStatus] = useState(() => searchParams.get('status') || 'all');
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [roleDetail, setRoleDetail] = useState(null);
  const [succession, setSuccession] = useState(null);

  const [removeIds, setRemoveIds] = useState([]);
  const [whatIf, setWhatIf] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const [cohorts, setCohorts] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState('');
  const [cohortSummary, setCohortSummary] = useState(null);
  const [cohortMembers, setCohortMembers] = useState([]);
  const [cohortRollup, setCohortRollup] = useState([]);
  const [dataStatus, setDataStatus] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [rollupOpen, setRollupOpen] = useState(() => {
    try { return localStorage.getItem('cap_rollup_open') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('cap_rollup_open', rollupOpen ? '1' : '0'); } catch { /* ignore */ }
  }, [rollupOpen]);

  const [memberOpen, setMemberOpen] = useState(false);
  const [memberData, setMemberData] = useState(null);

  const [briefOpen, setBriefOpen] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefData, setBriefData] = useState(null);
  const [briefTitle, setBriefTitle] = useState('');
  const [briefPayload, setBriefPayload] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [showEmail, setShowEmail] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatches, setDispatches] = useState([]);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [staleCount, setStaleCount] = useState(0);

  useEffect(() => {
    if (isSuperAdmin) api.get('/rbac/organizations').then(r => setOrgs(r.data.organizations || [])).catch(() => {});
  }, [isSuperAdmin]);
  useEffect(() => {
    if (!selectedOrg && orgs.length) setSelectedOrg(orgs[0].id);
  }, [orgs, selectedOrg]);

  const load = useCallback(async () => {
    if (!selectedOrg) { setLoading(false); return; }
    setLoading(true);
    try {
      const rolesUrl = `/capability-inventory/roles?organization_id=${selectedOrg}` +
        (selectedCohort ? `&cohort_id=${selectedCohort}` : '');
      const [s, r] = await Promise.all([
        api.get(`/capability-inventory/summary?organization_id=${selectedOrg}`),
        api.get(rolesUrl),
      ]);
      setSummary(s.data);
      setRoles(r.data.roles || []);
      setCohortSummary(r.data.cohort_summary || null);
      setCohortMembers(r.data.cohort_members || []);
      const list = r.data.roles || [];
      const first = list.find(x => x.risk_level !== 'healthy') || list[0];
      setSelectedRoleId(prev => (prev && list.some(x => x.role_id === prev)) ? prev : (first?.role_id || null));
    } catch (e) {
      toast.error('Could not load capability inventory');
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, selectedCohort]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedOrg) { setCohorts([]); return; }
    setSelectedCohort('');
    api.get(`/capability-inventory/cohorts?organization_id=${selectedOrg}`)
      .then(r => setCohorts(r.data.cohorts || [])).catch(() => setCohorts([]));
    api.get(`/capability-inventory/cohort-rollup?organization_id=${selectedOrg}`)
      .then(r => setCohortRollup(r.data.cohorts || [])).catch(() => setCohortRollup([]));
    api.get(`/capability-inventory/data-status?organization_id=${selectedOrg}`)
      .then(r => setDataStatus(r.data)).catch(() => setDataStatus(null));
  }, [selectedOrg]);

  useEffect(() => {
    if (!selectedOrg || !selectedRoleId) { setRoleDetail(null); setSuccession(null); return; }
    setRemoveIds([]); setWhatIf(null);
    Promise.all([
      api.get(`/capability-inventory/roles/${selectedRoleId}?organization_id=${selectedOrg}`),
      api.get(`/capability-inventory/roles/${selectedRoleId}/succession?organization_id=${selectedOrg}&limit=8`),
    ]).then(([d, su]) => { setRoleDetail(d.data); setSuccession(su.data); }).catch(() => {});
  }, [selectedOrg, selectedRoleId]);

  const runWhatIf = async () => {
    try {
      const res = await api.post(`/capability-inventory/roles/${selectedRoleId}/what-if`,
        { organization_id: selectedOrg, remove_member_ids: removeIds });
      setWhatIf(res.data);
    } catch { toast.error('What-if simulation failed'); }
  };

  const openMember = async (mid) => {
    setMemberOpen(true); setMemberData(null);
    try {
      const r = await api.get(`/capability-inventory/members/${mid}?organization_id=${selectedOrg}`);
      setMemberData(r.data);
    } catch { toast.error('Could not load member'); }
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      await api.post('/capability-inventory/seed', { organizations: selectedOrg ? [selectedOrg] : undefined });
      toast.success('Demo inventory generated');
      await load();
    } catch { toast.error('Seeding failed (super admin only)'); }
    finally { setSeeding(false); }
  };

  const toggleRemove = (mid) => setRemoveIds(prev => prev.includes(mid) ? prev.filter(x => x !== mid) : [...prev, mid]);

  // Drill from the Analytics heatmap into the Inventory view for a role
  // (optionally scoped to a cohort). load() preserves the selected role.
  const drillFromAnalytics = (roleId, cohortId) => {
    if (cohortId != null) setSelectedCohort(cohortId);
    setSelectedRoleId(roleId);
    changeView('inventory');
  };

  // One-click "Upskilling plan" export — exactly which skills to raise (and
  // by how much) to close the gap, as an L&D-ready CSV.
  const csvCell = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const downloadUpskillPlan = async (memberId, roleId = null) => {
    try {
      const q = new URLSearchParams({ organization_id: selectedOrg });
      if (roleId) q.set('role_id', roleId);
      const { data: plan } = await api.get(`/capability-inventory/members/${memberId}/upskilling-plan?${q.toString()}`);
      const rows = [['Member', 'Target Role', 'Skill', 'Family', 'Type', 'Current', 'Target', 'Lift', 'Target Level Descriptor']];
      plan.targets.forEach(t => t.gaps.forEach(g => rows.push([
        plan.name, t.role_name, g.skill_name, g.skill_family, g.skill_type,
        g.current_level, g.target_level, g.lift, g.target_descriptor || '',
      ])));
      if (rows.length === 1) { toast.info('No skill gaps — already at target for the selected role(s)'); return; }
      const csv = rows.map(r => r.map(csvCell).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${plan.name.replace(/\s+/g, '_')}_upskilling_plan.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Upskilling plan for ${plan.name} downloaded (${plan.total_skills_to_raise} skills)`);
    } catch { toast.error('Could not generate upskilling plan'); }
  };

  // Bridge the diagnostic gap → actionable L&D interventions. Deep-links into
  // Solution Finder on the Internal Mobility / Retention KPI, carrying the
  // member + target role so SF renders the "Capability gap" context banner.
  const findInterventions = (memberName, roleName = null, skillsShort = null, memberId = null, roleId = null) => {
    const q = new URLSearchParams({ kpi: 'mobility', cap_focus: memberName });
    if (roleName) q.set('cap_role', roleName);
    if (skillsShort != null) q.set('cap_gap', String(skillsShort));
    if (memberId) q.set('cap_member', memberId);
    if (roleId) q.set('cap_role_id', roleId);
    if (selectedOrg) q.set('organization_id', selectedOrg);
    navigate(`/solution-finder?${q.toString()}`);
  };

  // AI Impact & Action Plan brief (GPT-4o, server-grounded + cached).
  const runBrief = async (payload, regenerate = false) => {
    setBriefLoading(true);
    try {
      const { data } = await api.post('/capability-inventory/impact-brief',
        { organization_id: selectedOrg, ...payload, regenerate });
      setBriefData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate the impact brief');
      setBriefData(null);
    } finally {
      setBriefLoading(false);
    }
  };
  const openBrief = (payload, title) => {
    setBriefTitle(title);
    setBriefPayload(payload);
    setBriefData(null);
    setShowEmail(false);
    setEmailTo('');
    setEmailNote('');
    setBriefOpen(true);
    runBrief(payload, false);
  };
  const downloadBrief = () => {
    if (!briefData?.brief_markdown) return;
    const md = `# ${briefTitle}\n\n_Generated ${briefData.generated_at}_\n\n${briefData.brief_markdown}\n`;
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${briefTitle.replace(/[^a-z0-9]+/gi, '_')}_impact_brief.md`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const sendBriefEmail = async () => {
    if (!emailTo || !emailTo.includes('@')) { toast.error('Enter a valid email address'); return; }
    if (!briefData?.brief_markdown) return;
    setEmailSending(true);
    try {
      await api.post('/capability-inventory/impact-brief/email', {
        organization_id: selectedOrg,
        recipient_email: emailTo.trim(),
        title: briefTitle,
        mode: briefPayload?.mode,
        target_label: briefTitle.split(' — ')[0],
        brief_markdown: briefData.brief_markdown,
        note: emailNote.trim() || undefined,
      });
      toast.success(`Brief emailed to ${emailTo.trim()}`);
      setShowEmail(false);
      setEmailTo('');
      setEmailNote('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the email');
    } finally {
      setEmailSending(false);
    }
  };

  const loadDispatches = useCallback(async () => {
    setDispatchLoading(true);
    try {
      const { data } = await api.get('/capability-inventory/impact-brief/dispatches', {
        params: selectedOrg ? { organization_id: selectedOrg } : {},
      });
      setDispatches(data.dispatches || []);
      setOpenCount(data.open_count || 0);
      setStaleCount(data.stale_count || 0);
    } catch {
      toast.error('Could not load delegated plans');
    } finally {
      setDispatchLoading(false);
    }
  }, [selectedOrg]);
  const openDispatches = () => { setDispatchOpen(true); loadDispatches(); };
  useEffect(() => { loadDispatches(); }, [loadDispatches]);

  const relTime = (iso) => {
    if (!iso) return '';
    const days = (Date.now() - new Date(iso).getTime()) / 86400000;
    if (days < 1) return 'today';
    if (days < 2) return 'yesterday';
    if (days < 30) return `${Math.floor(days)} days ago`;
    return new Date(iso).toLocaleDateString();
  };
  const resetDemo = async () => {
    if (!window.confirm('Reset this org\u2019s capability data to the demo set? Any uploaded data will be replaced.')) return;
    setResetting(true);
    try {
      await api.post('/capability-inventory/reset-demo', { organization_id: selectedOrg });
      toast.success('Reset to demo data');
      await load();
      api.get(`/capability-inventory/data-status?organization_id=${selectedOrg}`).then(r => setDataStatus(r.data)).catch(() => {});
      api.get(`/capability-inventory/cohorts?organization_id=${selectedOrg}`).then(r => setCohorts(r.data.cohorts || [])).catch(() => {});
      api.get(`/capability-inventory/cohort-rollup?organization_id=${selectedOrg}`).then(r => setCohortRollup(r.data.cohorts || [])).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };
  const sendReminder = async (d) => {
    try {
      await api.post(`/capability-inventory/impact-brief/dispatches/${d.id}/remind`,
        { organization_id: selectedOrg });
      toast.success(`Reminder sent to ${d.sent_by_name || 'the delegator'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send reminder');
    }
  };
  const toggleDispatch = async (d) => {
    const next = d.status === 'open' ? 'followed_up' : 'open';
    try {
      await api.patch(`/capability-inventory/impact-brief/dispatches/${d.id}`,
        { organization_id: selectedOrg, status: next });
      setDispatches(prev => prev.map(x => x.id === d.id ? { ...x, status: next, stale: next === 'open' ? x.stale : false } : x));
      setOpenCount(prev => prev + (next === 'open' ? 1 : -1));
      if (d.stale && next === 'followed_up') setStaleCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update status');
    }
  };

  const heroActions = (
    <div className="flex items-center gap-2">
      {isSuperAdmin && orgs.length > 0 && (
        <Select value={selectedOrg} onValueChange={setSelectedOrg}>
          <SelectTrigger className="h-9 w-[190px] bg-white border-slate-200 text-sm" data-testid="cap-org-select">
            <SelectValue placeholder="Select organization" />
          </SelectTrigger>
          <SelectContent>
            {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {cohorts.length > 0 && (
        <Select value={selectedCohort || 'all'} onValueChange={(v) => setSelectedCohort(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-[200px] bg-white border-slate-200 text-sm" data-testid="cap-cohort-filter">
            <Users className="h-3.5 w-3.5 mr-1 text-slate-400" />
            <SelectValue placeholder="All cohorts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cohorts</SelectItem>
            {cohorts.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.member_count})</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      <Button variant="outline" size="sm" className="h-9 gap-1.5 relative" onClick={openDispatches} data-testid="cap-dispatches-btn">
        <ClipboardList className="h-4 w-4" /> Delegated plans
        {openCount > 0 && (
          <span className={`ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${staleCount > 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}
            title={staleCount > 0 ? `${staleCount} stale (open >14 days)` : `${openCount} open`}
            data-testid="cap-dispatches-open-badge">
            {staleCount > 0 ? `${openCount} · ${staleCount} stale` : openCount}
          </span>
        )}
      </Button>
      <PageToolbar theme={theme} organizationId={selectedOrg} testId="cap-toolbar" />
    </div>
  );

  const _hero = (
        <PageHero
          title="Capability Inventory"
          description="Who has what skills — aligned to job-role requirements. Drill from role coverage down to the individual, spot succession risk, and simulate a departure to see who can backfill."
          theme={theme}
          icon={ClipboardList}
          actions={heroActions}
          testId="capability-inventory-hero"
        >
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-400 pointer-events-none" aria-hidden="true" />
        </PageHero>
  );
  const _content = (<>
      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400" data-testid="cap-loading">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading inventory…
        </div>
      ) : !summary?.has_data ? (
        <Card className="border-2 border-dashed border-slate-200" data-testid="cap-empty">
          <CardContent className="py-16 text-center">
            <ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold">No capability inventory data yet</p>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              Import a skill catalog, job-role requirements and a workforce roster — or generate demo data to explore the experience.
            </p>
            {isSuperAdmin && (
              <Button className="mt-5" onClick={seedDemo} disabled={seeding} data-testid="cap-seed-demo-btn">
                {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2" />}
                Generate demo inventory
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* View toggle: Inventory | Analytics — hidden when embedded (the Capability Health sub-nav controls this) */}
          {!embedded && (
          <div className="inline-flex rounded-xl border border-slate-200 bg-slate-100 p-1" data-testid="cap-view-toggle">
            <button onClick={() => changeView('inventory')}
              className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg transition-all',
                view === 'inventory' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
              data-testid="cap-view-inventory">
              <ClipboardList className="h-4 w-4" /> Inventory
            </button>
            <button onClick={() => changeView('analytics')}
              className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg transition-all',
                view === 'analytics' ? 'bg-white text-cyan-700 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
              data-testid="cap-view-analytics">
              <LineChart className="h-4 w-4" /> Analytics
            </button>
          </div>
          )}

          {/* Summary tiles — coverage-tailored set when embedded as the Coverage tab. */}
          {coverageMode ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cap-coverage-tiles">
              <StatTile icon={Target} tone={summary.role_coverage_pct >= 90 ? 'emerald' : summary.role_coverage_pct >= 60 ? 'amber' : 'rose'} label="Role Coverage" value={`${summary.role_coverage_pct}%`} sub={`${summary.roles_fully_staffed}/${summary.total_roles} roles have a ready holder`} testId="cap-tile-coverage" />
              <StatTile icon={ShieldAlert} tone={summary.roles_at_risk > 0 ? 'rose' : 'emerald'} label="Roles at Risk" value={summary.roles_at_risk} sub="0–1 ready holder — single point of failure" testId="cap-tile-risk" />
              <StatTile icon={CheckCircle2} tone={(summary.total_roles - summary.roles_at_risk) >= summary.total_roles ? 'emerald' : 'cyan'} label="Deep Bench" value={`${Math.max(0, summary.total_roles - summary.roles_at_risk)}/${summary.total_roles}`} sub="roles with ≥2 ready holders" testId="cap-tile-staffed" />
              <StatTile icon={Users} tone={summary.member_coverage_pct >= 75 ? 'emerald' : 'amber'} label="People Role-Ready" value={`${summary.member_coverage_pct}%`} sub={`${summary.total_members} members assessed`} testId="cap-tile-members" />
            </div>
          ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cap-inventory-tiles">
            <StatTile icon={Target} tone={summary.role_coverage_pct >= 90 ? 'emerald' : 'amber'} label="Role Coverage" value={`${summary.role_coverage_pct}%`} sub={`${summary.roles_fully_staffed}/${summary.total_roles} roles have a ready holder`} testId="cap-tile-coverage" />
            <StatTile icon={ShieldAlert} tone={summary.roles_at_risk > 0 ? 'rose' : 'emerald'} label="Roles at Risk" value={summary.roles_at_risk} sub="0–1 ready holder" testId="cap-tile-risk" />
            <StatTile icon={Users} tone="cyan" label="Members" value={summary.total_members} sub={`${summary.member_coverage_pct}% cover their role`} testId="cap-tile-members" />
            <StatTile icon={Layers} tone="slate" label="Skills Tracked" value={summary.total_skills} sub="across families" testId="cap-tile-skills" />
          </div>
          )}

          {!embedded && dataStatus?.has_data && (
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 text-xs" data-testid="cap-data-status">
              <span className={cn('inline-flex items-center gap-1.5 font-semibold px-2 py-0.5 rounded-full',
                dataStatus.source === 'uploaded' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600')}>
                <Database className="h-3 w-3" />
                {dataStatus.source === 'uploaded' ? 'Uploaded data' : 'Demo data'}
              </span>
              <span className="text-slate-500">
                {dataStatus.source === 'uploaded'
                  ? (dataStatus.last_import_at ? `last import ${relTime(dataStatus.last_import_at)}` : 'imported')
                  : (dataStatus.last_seeded_at ? `seeded ${relTime(dataStatus.last_seeded_at)}` : 'synthetic')}
              </span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-600 tabular-nums">{dataStatus.counts.members} members · {dataStatus.counts.roles} roles · {dataStatus.counts.skills} skills</span>
              {dataStatus.partial && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium" data-testid="cap-partial-warning"
                  title={`Still demo: ${(dataStatus.partial_demo_kinds || []).join(', ')}. This inventory mixes uploaded and demo data.`}>
                  <AlertTriangle className="h-3 w-3" /> Partial import — {(dataStatus.partial_demo_kinds || []).map(k => k.replace('_', ' ')).join(', ')} still demo
                </span>
              )}
              {dataStatus.can_admin && dataStatus.can_seed_demo && (
                <Button size="sm" variant="ghost" className="h-6 ml-auto text-[11px] text-slate-500 hover:text-slate-700 gap-1"
                  onClick={resetDemo} disabled={resetting} data-testid="cap-reset-demo-btn">
                  {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  Reset to demo data
                </Button>
              )}
            </div>
          )}

          {view === 'analytics' && (
            <CapabilityAnalytics
              selectedOrg={selectedOrg}
              canAdmin={dataStatus?.can_admin}
              onDrill={drillFromAnalytics}
            />
          )}

          {view === 'inventory' && (<>
          {!selectedCohort && cohortRollup.length > 0 && (() => {
            const totMembers = cohortRollup.reduce((a, c) => a + c.member_count, 0);
            const overall = totMembers
              ? Math.round(cohortRollup.reduce((a, c) => a + c.role_coverage_pct * c.member_count, 0) / totMembers)
              : 0;
            const tone = overall > 75 ? 'emerald' : overall <= 35 ? 'rose' : 'amber';
            const wrap = {
              emerald: 'border-emerald-200 bg-emerald-50/50',
              amber: 'border-amber-200 bg-amber-50/50',
              rose: 'border-rose-200 bg-rose-50/50',
            }[tone];
            const dot = { emerald: 'bg-emerald-500', amber: 'bg-amber-500', rose: 'bg-rose-500' }[tone];
            const txt = { emerald: 'text-emerald-700', amber: 'text-amber-700', rose: 'text-rose-700' }[tone];
            return (
            <div data-testid="cap-cohort-rollup">
              <button onClick={() => setRollupOpen(v => !v)}
                className={cn('w-full flex items-center gap-3 rounded-xl border px-4 py-2.5 transition-colors hover:brightness-[0.98]', wrap)}
                data-testid="cap-cohort-rollup-toggle">
                <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', dot)} />
                <Users className="h-4 w-4 text-slate-500 shrink-0" />
                <span className="text-sm font-semibold text-slate-800">Cohort bench health</span>
                <span className="text-sm text-slate-600">
                  · {cohortRollup.length} cohorts · <strong className={cn('tabular-nums', txt)}>{overall}%</strong> overall bench health
                </span>
                <span className="ml-auto flex items-center gap-1 text-xs text-slate-500">
                  {rollupOpen ? 'Collapse' : 'Expand to view'}
                  <ChevronDown className={cn('h-4 w-4 transition-transform', rollupOpen ? '' : '-rotate-90')} />
                </span>
              </button>
              {rollupOpen && (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mt-3">
                {cohortRollup.map(c => {
                  const cov = c.role_coverage_pct;
                  const t = cov >= 85 ? 'emerald' : cov >= 60 ? 'amber' : 'rose';
                  const bar = { emerald: 'bg-emerald-500', amber: 'bg-amber-500', rose: 'bg-rose-500' }[t];
                  const ctxt = { emerald: 'text-emerald-700', amber: 'text-amber-700', rose: 'text-rose-700' }[t];
                  return (
                    <button key={c.cohort_id} onClick={() => setSelectedCohort(c.cohort_id)}
                      className="text-left rounded-xl border border-slate-200 bg-white p-3 hover:border-cyan-300 hover:shadow-sm transition-all"
                      data-testid={`cap-rollup-${c.cohort_id}`}>
                      <p className="text-xs font-semibold text-slate-800 truncate" title={c.cohort_name}>{c.cohort_name}</p>
                      <p className="text-[10px] text-slate-400 mb-2">{c.member_count} members · {c.roles_applying} roles</p>
                      <div className="flex items-baseline gap-1">
                        <span className={cn('text-2xl font-bold tabular-nums', ctxt)}>{cov}%</span>
                        <span className="text-[10px] text-slate-400">role coverage</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={cn('h-full rounded-full', bar)} style={{ width: `${cov}%` }} />
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-[10px]">
                        <span className={cn('px-1.5 py-0.5 rounded-full', c.roles_at_risk > 0 ? 'bg-amber-50 text-amber-700' : 'bg-slate-50 text-slate-500')}>{c.roles_at_risk} at-risk</span>
                        <span className={cn('px-1.5 py-0.5 rounded-full', c.succession_gaps > 0 ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700')}>{c.succession_gaps} gaps</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              )}
            </div>
            );
          })()}

          {cohortSummary && (
            <div className="rounded-xl border border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50/60 p-4" data-testid="cap-cohort-summary">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-cyan-700" />
                  <span className="text-sm font-semibold text-cyan-900">Cohort lens · {cohortSummary.cohort_name}</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-700">
                  {typeof cohortSummary.role_coverage_pct === 'number' && (() => {
                    const cov = cohortSummary.role_coverage_pct;
                    const txt = cov >= 85 ? 'text-emerald-700' : cov >= 60 ? 'text-amber-700' : 'text-rose-700';
                    return (
                      <span className="flex items-baseline gap-1" data-testid="cap-cohort-coverage">
                        <strong className={cn('text-xl font-bold tabular-nums', txt)}>{cov}%</strong>
                        <span className="text-[11px] text-slate-500">role coverage</span>
                      </span>
                    );
                  })()}
                  <span><strong className="text-cyan-800 tabular-nums">{cohortSummary.member_count}</strong> members</span>
                  <span><strong className="text-cyan-800 tabular-nums">{cohortSummary.role_count}</strong> job roles apply</span>
                  {cohortSummary.succession_gaps > 0 && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">{cohortSummary.succession_gaps} succession gaps</span>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 hover:text-slate-700" onClick={() => setSelectedCohort('')} data-testid="cap-cohort-clear">Clear</Button>
                </div>
              </div>
              {cohortMembers.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="cap-cohort-members">
                  {cohortMembers.map(m => (
                    <button key={m.member_id} onClick={() => openMember(m.member_id)}
                      className="text-[11px] px-2 py-1 rounded-full bg-white border border-cyan-200 text-slate-700 hover:border-cyan-400 transition-colors"
                      title={`${m.department || ''}${m.cohorts?.length > 1 ? ' · ' + m.cohorts.join(', ') : ''}`}
                      data-testid={`cap-cohort-member-${m.member_id}`}>
                      {m.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Role list */}
            <div className="lg:col-span-1 space-y-2" data-testid="cap-role-list">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-1">Job Roles</p>
              <div className="space-y-2 px-0.5" data-testid="cap-role-filters">
                <input value={roleSearch} onChange={(e) => setRoleSearch(e.target.value)} placeholder="Search role or family…"
                  data-testid="cap-role-search"
                  className="w-full text-sm rounded-lg px-3 py-1.5 border bg-white text-slate-900 border-slate-300 placeholder-slate-400 focus:outline-none focus:border-cyan-400 dark:bg-white/5 dark:text-slate-100 dark:border-white/15 dark:placeholder-slate-500 dark:focus:border-cyan-400" />
                <div className="flex gap-1.5 flex-wrap">
                  {[['all', 'All'], ['healthy', 'Healthy'], ['at_risk', 'At Risk'], ['critical', 'Critical'], ['at_risk,critical', 'At Risk + Critical']].map(([v, lbl]) => (
                    <button key={v} onClick={() => setRoleStatus(v)} data-testid={`cap-role-status-${v}`}
                      className={cn('text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors',
                        roleStatus === v ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-slate-500 border-slate-200 hover:border-cyan-400')}>{lbl}</button>
                  ))}
                </div>
              </div>
              {(() => {
                const q = roleSearch.trim().toLowerCase();
                // Coverage-based status: Healthy ≥100%, At Risk 85–<100%, Critical <85%.
                const statusOf = (cov) => (cov < 85 ? 'critical' : cov < 100 ? 'at_risk' : 'healthy');
                const visibleRoles = roles.filter((r) =>
                  (roleStatus === 'all' || roleStatus.split(',').includes(statusOf(r.avg_coverage_pct ?? 0))) &&
                  (!q || (r.role_name || '').toLowerCase().includes(q) || (r.role_family || '').toLowerCase().includes(q)));
                if (!visibleRoles.length) return <p className="text-xs text-slate-400 px-1 py-4" data-testid="cap-role-empty">No roles match this filter.</p>;
                return visibleRoles.map(r => {
                const rk = RISK[statusOf(r.avg_coverage_pct ?? 0)] || RISK.healthy;
                const active = r.role_id === selectedRoleId;
                return (
                  <button
                    key={r.role_id}
                    onClick={() => setSelectedRoleId(r.role_id)}
                    className={cn('w-full text-left rounded-lg border p-3 transition-colors',
                      active ? 'border-blue-300 bg-blue-50/50 ring-1 ring-blue-200' : 'border-slate-200 bg-white hover:border-slate-300')}
                    data-testid={`cap-role-item-${r.role_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-800 truncate">{r.role_name}</span>
                      <Badge variant="outline" className={cn('text-[10px] shrink-0', rk.badge)}>{rk.label}</Badge>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">{r.role_family}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <CoverageBar pct={r.avg_coverage_pct} />
                      <span className="text-[11px] text-slate-500 tabular-nums shrink-0">{r.avg_coverage_pct}%</span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">{r.ready_count}/{r.assigned_count} ready · {r.required_skills} skills</p>
                  </button>
                );
              });
              })()}
            </div>

            {/* Role detail */}
            <div className="lg:col-span-2 space-y-4">
              {!roleDetail ? (
                <div className="text-slate-400 text-sm py-10 text-center">Select a role to see details.</div>
              ) : (
                <>
                  {/* Readiness header */}
                  <Card className="border border-slate-200 shadow-sm" data-testid="cap-role-detail">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <CardTitle className="text-lg">{roleDetail.role_name}</CardTitle>
                          <p className="text-xs text-slate-500 mt-0.5">{roleDetail.role_family}</p>
                        </div>
                        <Badge variant="outline" className={cn(RISK[(roleDetail.readiness.avg_coverage_pct ?? 0) < 85 ? 'critical' : (roleDetail.readiness.avg_coverage_pct ?? 0) < 100 ? 'at_risk' : 'healthy']?.badge)}>
                          {RISK[(roleDetail.readiness.avg_coverage_pct ?? 0) < 85 ? 'critical' : (roleDetail.readiness.avg_coverage_pct ?? 0) < 100 ? 'at_risk' : 'healthy']?.label}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="grid grid-cols-3 gap-4 text-center">
                      <div><p className="text-2xl font-extrabold text-slate-900">{roleDetail.readiness.ready_count}</p><p className="text-[11px] text-slate-500 uppercase">Ready Holders</p></div>
                      <div><p className="text-2xl font-extrabold text-slate-900">{roleDetail.readiness.assigned_count}</p><p className="text-[11px] text-slate-500 uppercase">Assigned</p></div>
                      <div><p className="text-2xl font-extrabold text-slate-900">{roleDetail.readiness.avg_coverage_pct}%</p><p className="text-[11px] text-slate-500 uppercase">Avg Coverage</p></div>
                    </CardContent>
                  </Card>

                  {/* Skill heatmap */}
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Required Skills — proficiency vs target</CardTitle></CardHeader>
                    <CardContent className="space-y-2" data-testid="cap-skill-heatmap">
                      {roleDetail.skill_heatmap.map(s => {
                        const pct = s.target_level ? Math.min(100, (s.avg_current / s.target_level) * 100) : 0;
                        return (
                          <div key={s.skill_id} className="grid grid-cols-[1fr_auto] gap-2 items-center">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-700">{s.skill_name}</span>
                                <span className={cn('text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wide', s.skill_type === 'technical' ? 'bg-indigo-50 text-indigo-600' : 'bg-teal-50 text-teal-600')}>{s.skill_type}</span>
                              </div>
                              <CoverageBar pct={pct} className="mt-1" />
                            </div>
                            <div className="text-right whitespace-nowrap">
                              <span className="text-xs font-semibold text-slate-700 tabular-nums">{s.avg_current} / {s.target_level}</span>
                              <p className="text-[10px] text-slate-400">{s.holders_at_target}/{s.holders_total} at target</p>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>

                  {/* Holders + what-if */}
                  <Card className="border border-slate-200 shadow-sm">
                    <CardHeader className="pb-2 flex-row items-center justify-between">
                      <CardTitle className="text-sm">Current Holders</CardTitle>
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={runWhatIf} disabled={removeIds.length === 0} data-testid="cap-whatif-run">
                        <ShieldAlert className="h-3.5 w-3.5" /> Simulate departure ({removeIds.length})
                      </Button>
                    </CardHeader>
                    <CardContent className="space-y-1.5" data-testid="cap-holders">
                      {roleDetail.holders.length === 0 && <p className="text-xs text-slate-400">No one is currently assigned to this role.</p>}
                      {roleDetail.holders.map(h => (
                        <div key={h.member_id} className="flex items-center gap-2.5 py-1.5 border-b border-slate-100 last:border-0">
                          <Checkbox checked={removeIds.includes(h.member_id)} onCheckedChange={() => toggleRemove(h.member_id)} data-testid={`cap-holder-check-${h.member_id}`} />
                          <button onClick={() => openMember(h.member_id)} className="text-sm text-slate-700 hover:text-blue-700 font-medium flex-1 text-left truncate" data-testid={`cap-holder-${h.member_id}`}>
                            {h.name}
                          </button>
                          <CohortTags cohorts={h.cohorts} testId={`cap-holder-cohorts-${h.member_id}`} />
                          {h.covered
                            ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200"><ShieldCheck className="h-3 w-3 mr-0.5" />Ready</Badge>
                            : <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{h.met_count}/{h.required_count}</Badge>}
                          <span className="text-xs text-slate-500 tabular-nums w-12 text-right">{h.coverage_pct}%</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* What-if result */}
                  {whatIf && (
                    <Card className={cn('border shadow-sm', whatIf.risk_after === 'critical' ? 'border-rose-200 bg-rose-50/40' : whatIf.risk_after === 'at_risk' ? 'border-amber-200 bg-amber-50/40' : 'border-emerald-200 bg-emerald-50/40')} data-testid="cap-whatif-result">
                      <CardHeader className="pb-2 flex-row items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> Departure impact</CardTitle>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1.5 border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                            onClick={() => openBrief(
                              { mode: 'what-if', role_id: selectedRoleId, remove_member_ids: removeIds },
                              `${roleDetail?.role_name || 'Role'} — departure impact`)}
                            data-testid="cap-whatif-ai-brief">
                            <Sparkles className="h-3.5 w-3.5" /> AI impact brief
                          </Button>
                          <button onClick={() => { setWhatIf(null); setRemoveIds([]); }} className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-slate-700">
                          Removing {whatIf.removed.length} holder{whatIf.removed.length !== 1 ? 's' : ''} takes ready holders from{' '}
                          <b>{whatIf.ready_before}</b> → <b>{whatIf.ready_after}</b> —{' '}
                          <span className={cn('font-semibold', whatIf.risk_after === 'critical' ? 'text-rose-700' : whatIf.risk_after === 'at_risk' ? 'text-amber-700' : 'text-emerald-700')}>
                            {RISK[whatIf.risk_after]?.label}
                          </span>.
                        </p>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-3 mb-1">Recommended backfill</p>
                        <div className="space-y-1">
                          {whatIf.backfill_candidates.slice(0, 5).map(c => (
                            <div key={c.member_id} className="text-sm">
                              <div className="flex items-center gap-2">
                              <button onClick={() => openMember(c.member_id)} className="text-slate-700 hover:text-blue-700 flex-1 text-left truncate">{c.name}</button>
                              {c.ready_now && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Ready now</Badge>}
                              <span className="text-xs text-slate-500 tabular-nums">{c.coverage_pct}%</span>
                              {!c.ready_now && <span className="text-[11px] text-slate-400">+{c.skills_short} skills</span>}
                              {!c.ready_now && (
                                <button onClick={() => downloadUpskillPlan(c.member_id, selectedRoleId)}
                                  className="text-slate-400 hover:text-blue-700" title="Download upskilling plan for this role"
                                  data-testid={`cap-whatif-plan-${c.member_id}`}>
                                  <Download className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {!c.ready_now && (
                                <button onClick={() => findInterventions(c.name, roleDetail?.role_name, c.skills_short, c.member_id, selectedRoleId)}
                                  className="text-slate-400 hover:text-cyan-700" title="Find L&D interventions in Solution Finder"
                                  data-testid={`cap-whatif-find-${c.member_id}`}>
                                  <Compass className="h-3.5 w-3.5" />
                                </button>
                              )}
                              </div>
                              <RecChips items={c.recommended_interventions} testId={`cap-whatif-recs-${c.member_id}`} />
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Succession bench */}
                  {succession && (
                    <Card className="border border-slate-200 shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Succession Bench</CardTitle>
                        <p className="text-[11px] text-slate-500">{succession.ready_now} ready now · others ranked by gap-to-close</p>
                      </CardHeader>
                      <CardContent className="space-y-1.5" data-testid="cap-succession">
                        {succession.candidates.map(c => (
                          <div key={c.member_id} className="py-1.5 border-b border-slate-100 last:border-0">
                            <div className="flex items-center gap-2.5">
                            <UserRound className="h-4 w-4 text-slate-400 shrink-0" />
                            <button onClick={() => openMember(c.member_id)} className="text-sm text-slate-700 hover:text-blue-700 font-medium flex-1 text-left truncate">{c.name}</button>
                            {c.ready_now
                              ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Ready now</Badge>
                              : <span className="text-[11px] text-slate-400">+{c.skills_short} skills</span>}
                            <span className="text-xs text-slate-500 tabular-nums w-12 text-right">{c.coverage_pct}%</span>
                            {c.ready_now
                              ? <ChevronRight className="h-4 w-4 text-slate-300" />
                              : <>
                                  <button onClick={(e) => { e.stopPropagation(); downloadUpskillPlan(c.member_id, selectedRoleId); }}
                                    className="text-slate-400 hover:text-blue-700" title="Download upskilling plan for this role"
                                    data-testid={`cap-succession-plan-${c.member_id}`}>
                                    <Download className="h-4 w-4" />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); findInterventions(c.name, roleDetail?.role_name, c.skills_short, c.member_id, selectedRoleId); }}
                                    className="text-slate-400 hover:text-cyan-700" title="Find L&D interventions in Solution Finder"
                                    data-testid={`cap-succession-find-${c.member_id}`}>
                                    <Compass className="h-4 w-4" />
                                  </button>
                                </>}
                            </div>
                            <RecChips items={c.recommended_interventions} testId={`cap-succession-recs-${c.member_id}`} />
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
          </div>
          </>)}
        </div>
      )}

      {/* Member drawer */}
      <MemberDrawer
        open={memberOpen}
        onOpenChange={setMemberOpen}
        memberData={memberData}
        onOpenBrief={openBrief}
        onDownloadUpskill={downloadUpskillPlan}
        onFindInterventions={findInterventions}
      />

      {/* Delegated plans (audit trail) */}
      <DelegatedPlansDialog
        open={dispatchOpen}
        onOpenChange={setDispatchOpen}
        loading={dispatchLoading}
        dispatches={dispatches}
        onRemind={sendReminder}
        onToggle={toggleDispatch}
      />

      {/* AI Impact & Action Plan brief */}
      <ImpactBriefDialog
        open={briefOpen}
        onOpenChange={setBriefOpen}
        briefLoading={briefLoading}
        briefData={briefData}
        briefTitle={briefTitle}
        briefPayload={briefPayload}
        showEmail={showEmail}
        emailTo={emailTo}
        emailNote={emailNote}
        emailSending={emailSending}
        onRegenerate={runBrief}
        onToggleEmail={() => setShowEmail(v => !v)}
        onDownload={downloadBrief}
        onEmailToChange={setEmailTo}
        onEmailNoteChange={setEmailNote}
        onSendEmail={sendBriefEmail}
      />
    </>);
  if (embedded) return <div className="space-y-6">{_content}</div>;
  return (
    <PageShell theme={theme} stickyTitle="Capability Inventory" hero={_hero}>
      {_content}
    </PageShell>
  );
}
